import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { Account } from '../../hooks/useAccounts';
import { useAuth } from '../../context/AuthContext';
import useHousehold from '../../hooks/useHousehold';
import { authFetch } from '../../lib/authFetch';

interface Props {
  open: boolean;
  onClose: () => void;
  creditCardAccounts: Account[];
  token: string | null;
  onSaved: () => void;
}

const parseOptionalCreditLimit = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  if (Number.isNaN(parsed)) return Number.NaN;
  return parsed;
};

const parseOptionalDayOfMonth = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  if (Number.isNaN(parsed)) return Number.NaN;
  return parsed;
};

const parseRequiredOpeningBalance = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  if (Number.isNaN(parsed)) return Number.NaN;
  return parsed;
};

const validateStatementCycleDays = (
  statementCloseDayValue: string,
  paymentDueDayValue: string
):
  | {
      statementCloseDay?: number;
      paymentDueDay?: number;
    }
  | {
      error: string;
    } => {
  const statementCloseDay = parseOptionalDayOfMonth(statementCloseDayValue);
  const paymentDueDay = parseOptionalDayOfMonth(paymentDueDayValue);

  const hasStatementCloseDay = statementCloseDay !== undefined;
  const hasPaymentDueDay = paymentDueDay !== undefined;

  if (hasStatementCloseDay !== hasPaymentDueDay) {
    return { error: 'Statement close day and payment due day must both be provided.' };
  }

  if (!hasStatementCloseDay && !hasPaymentDueDay) {
    return {};
  }

  if (
    Number.isNaN(statementCloseDay) ||
    !Number.isInteger(statementCloseDay) ||
    (statementCloseDay as number) < 1 ||
    (statementCloseDay as number) > 28
  ) {
    return { error: 'Statement close day must be an integer between 1 and 28.' };
  }

  if (
    Number.isNaN(paymentDueDay) ||
    !Number.isInteger(paymentDueDay) ||
    (paymentDueDay as number) < 1 ||
    (paymentDueDay as number) > 28
  ) {
    return { error: 'Payment due day must be an integer between 1 and 28.' };
  }

  return { statementCloseDay, paymentDueDay };
};

const ManageCreditCardsModal: React.FC<Props> = ({
  open,
  onClose,
  creditCardAccounts,
  token,
  onSaved,
}) => {
  const { auth } = useAuth();
  const { loading: householdLoading, isEmpty: householdEmpty } = useHousehold();
  const [newName, setNewName] = useState('');
  const [newAccessScope, setNewAccessScope] = useState<'private' | 'household'>('private');
  const [newOpeningBalance, setNewOpeningBalance] = useState('');
  const [newCreditLimit, setNewCreditLimit] = useState('');
  const [newStatementCloseDay, setNewStatementCloseDay] = useState('');
  const [newPaymentDueDay, setNewPaymentDueDay] = useState('');
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editAccessScope, setEditAccessScope] = useState<'private' | 'household'>('private');
  const [editCreditLimit, setEditCreditLimit] = useState('');
  const [editStatementCloseDay, setEditStatementCloseDay] = useState('');
  const [editPaymentDueDay, setEditPaymentDueDay] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const isChildUser = auth.user?.role === 'child';
  const canShareWithHousehold = !isChildUser && !householdLoading && !householdEmpty;
  const sharingHelperText = isChildUser
    ? 'Only Primary/Partner can change sharing.'
    : householdEmpty
      ? 'If you need household sharing enabled, please contact support.'
      : householdLoading
        ? 'Loading household...'
        : null;

  const editingAccount = useMemo(
    () => creditCardAccounts.find((account) => account._id === editingAccountId) || null,
    [creditCardAccounts, editingAccountId]
  );

  const handleStartEdit = (account: Account) => {
    setEditingAccountId(account._id);
    setEditName(account.name);
    setEditAccessScope(account.accessScope === 'household' ? 'household' : 'private');
    setEditCreditLimit(
      typeof account.creditLimit === 'number' && Number.isFinite(account.creditLimit)
        ? String(account.creditLimit)
        : ''
    );
    setEditStatementCloseDay(
      typeof account.statementCloseDay === 'number' && Number.isFinite(account.statementCloseDay)
        ? String(account.statementCloseDay)
        : ''
    );
    setEditPaymentDueDay(
      typeof account.paymentDueDay === 'number' && Number.isFinite(account.paymentDueDay)
        ? String(account.paymentDueDay)
        : ''
    );
    setError(null);
  };

  const handleCancelEdit = () => {
    setEditingAccountId(null);
    setEditName('');
    setEditAccessScope('private');
    setEditCreditLimit('');
    setEditStatementCloseDay('');
    setEditPaymentDueDay('');
    setError(null);
  };

  const handleAddCard = async () => {
    if (!token) return;

    const trimmedName = newName.trim();
    const parsedOpeningBalance = parseRequiredOpeningBalance(newOpeningBalance);
    const parsedCreditLimit = parseOptionalCreditLimit(newCreditLimit);
    const statementCycleValidation = validateStatementCycleDays(newStatementCloseDay, newPaymentDueDay);
    if (!trimmedName) {
      setError('Name is required.');
      return;
    }
    if (parsedOpeningBalance === undefined) {
      setError('Current balance owed as of today is required.');
      return;
    }
    if (Number.isNaN(parsedOpeningBalance)) {
      setError('Current balance owed as of today must be a valid number.');
      return;
    }
    if (parsedOpeningBalance < 0) {
      setError('Balance owed cannot be negative.');
      return;
    }
    if (
      parsedCreditLimit !== undefined &&
      (Number.isNaN(parsedCreditLimit) || parsedCreditLimit < 0)
    ) {
      setError('Credit limit must be a non-negative number.');
      return;
    }
    if ('error' in statementCycleValidation) {
      setError(statementCycleValidation.error);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        name: trimmedName,
        openingBalance: parsedOpeningBalance,
        accountType: 'creditCard',
        accountCategory: 'liability',
        accessScope: canShareWithHousehold ? newAccessScope : 'private',
      };
      if (parsedCreditLimit !== undefined) {
        payload.creditLimit = parsedCreditLimit;
      }
      if (
        statementCycleValidation.statementCloseDay !== undefined &&
        statementCycleValidation.paymentDueDay !== undefined
      ) {
        payload.statementCloseDay = statementCycleValidation.statementCloseDay;
        payload.paymentDueDay = statementCycleValidation.paymentDueDay;
      }
      const res = await authFetch('/api/accounts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({} as { message?: string }));
        throw new Error(data.message || 'Failed to create credit card account');
      }
      setNewName('');
      setNewAccessScope('private');
      setNewOpeningBalance('');
      setNewCreditLimit('');
      setNewStatementCloseDay('');
      setNewPaymentDueDay('');
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create credit card account');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!token || !editingAccount) return;

    const trimmedName = editName.trim();
    const parsedCreditLimit = parseOptionalCreditLimit(editCreditLimit);
    const statementCycleValidation = validateStatementCycleDays(
      editStatementCloseDay,
      editPaymentDueDay
    );
    if (!trimmedName) {
      setError('Name is required.');
      return;
    }
    if (
      parsedCreditLimit !== undefined &&
      (Number.isNaN(parsedCreditLimit) || parsedCreditLimit < 0)
    ) {
      setError('Credit limit must be a non-negative number.');
      return;
    }
    if ('error' in statementCycleValidation) {
      setError(statementCycleValidation.error);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        name: trimmedName,
        accessScope:
          !householdEmpty && editAccessScope === 'household' ? 'household' : 'private',
      };
      if (parsedCreditLimit !== undefined) {
        payload.creditLimit = parsedCreditLimit;
      }
      if (
        statementCycleValidation.statementCloseDay !== undefined &&
        statementCycleValidation.paymentDueDay !== undefined
      ) {
        payload.statementCloseDay = statementCycleValidation.statementCloseDay;
        payload.paymentDueDay = statementCycleValidation.paymentDueDay;
      }
      const res = await authFetch(`/api/accounts/${editingAccount._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({} as { message?: string }));
        throw new Error(data.message || 'Failed to update credit card account');
      }
      handleCancelEdit();
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update credit card account');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Manage Cards
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography fontWeight={600} sx={{ mb: 1 }}>
          Add Credit Card
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Use 1-28 to avoid short months.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <TextField
            fullWidth
            size="small"
            label="Current balance owed as of today"
            helperText="Snapshot amount used as today's baseline."
            type="number"
            required
            value={newOpeningBalance}
            onChange={(e) => setNewOpeningBalance(e.target.value)}
          />
          <TextField
            fullWidth
            size="small"
            label="Credit Limit"
            type="number"
            value={newCreditLimit}
            onChange={(e) => setNewCreditLimit(e.target.value)}
          />
          <TextField
            fullWidth
            size="small"
            label="Statement close day (1-28)"
            type="number"
            inputProps={{ min: 1, max: 28, step: 1 }}
            value={newStatementCloseDay}
            onChange={(e) => setNewStatementCloseDay(e.target.value)}
          />
          <TextField
            fullWidth
            size="small"
            label="Payment due day (1-28)"
            type="number"
            inputProps={{ min: 1, max: 28, step: 1 }}
            value={newPaymentDueDay}
            onChange={(e) => setNewPaymentDueDay(e.target.value)}
          />
          <Button variant="contained" onClick={handleAddCard} disabled={saving || !token}>
            Add
          </Button>
        </Stack>
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25, mb: 2 }}>
          <FormControl component="fieldset" disabled={isChildUser || saving} fullWidth>
            <FormLabel component="legend">Sharing</FormLabel>
            <RadioGroup
              value={newAccessScope}
              onChange={(event) =>
                setNewAccessScope(event.target.value as 'private' | 'household')
              }
            >
              <FormControlLabel value="private" control={<Radio />} label="Private (only me)" />
              <FormControlLabel
                value="household"
                control={<Radio />}
                label="Shared with household"
                disabled={!canShareWithHousehold}
              />
            </RadioGroup>
          </FormControl>
          {sharingHelperText && (
            <Typography variant="body2" color="text.secondary">
              {sharingHelperText}
            </Typography>
          )}
        </Box>

        <Typography fontWeight={600} sx={{ mb: 1 }}>
          Existing Cards
        </Typography>
        {creditCardAccounts.length === 0 ? (
          <Typography color="text.secondary">No credit card accounts yet.</Typography>
        ) : (
          <Stack spacing={1}>
            {creditCardAccounts.map((account) => (
              <Box
                key={account._id}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25 }}
              >
                {editingAccountId === account._id ? (
                  <Stack spacing={1.25}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Name"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Credit Limit"
                        type="number"
                        value={editCreditLimit}
                        onChange={(e) => setEditCreditLimit(e.target.value)}
                      />
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Statement close day (1-28)"
                        type="number"
                        inputProps={{ min: 1, max: 28, step: 1 }}
                        value={editStatementCloseDay}
                        onChange={(e) => setEditStatementCloseDay(e.target.value)}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Payment due day (1-28)"
                        type="number"
                        inputProps={{ min: 1, max: 28, step: 1 }}
                        value={editPaymentDueDay}
                        onChange={(e) => setEditPaymentDueDay(e.target.value)}
                      />
                    </Stack>

                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 1.25,
                      }}
                    >
                      <FormControl component="fieldset" disabled={isChildUser || saving} fullWidth>
                        <FormLabel component="legend">Sharing</FormLabel>
                        <RadioGroup
                          value={editAccessScope}
                          onChange={(event) =>
                            setEditAccessScope(event.target.value as 'private' | 'household')
                          }
                        >
                          <FormControlLabel
                            value="private"
                            control={<Radio />}
                            label="Private (only me)"
                          />
                          <FormControlLabel
                            value="household"
                            control={<Radio />}
                            label="Shared with household"
                            disabled={!canShareWithHousehold}
                          />
                        </RadioGroup>
                      </FormControl>
                      {sharingHelperText && (
                        <Typography variant="body2" color="text.secondary">
                          {sharingHelperText}
                        </Typography>
                      )}
                    </Box>

                    <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
                      <Button variant="contained" onClick={handleSaveEdit} disabled={saving}>
                        Save
                      </Button>
                      <Button onClick={handleCancelEdit} disabled={saving}>
                        Cancel
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography fontWeight={600}>{account.name}</Typography>
                      {typeof account.creditLimit === 'number' && (
                        <Typography variant="body2" color="text.secondary">
                          Limit: ${account.creditLimit.toFixed(2)}
                        </Typography>
                      )}
                    </Box>
                    <Button size="small" onClick={() => handleStartEdit(account)}>
                      Edit
                    </Button>
                  </Stack>
                )}
              </Box>
            ))}
          </Stack>
        )}

        {error && (
          <Typography color="error.main" sx={{ mt: 1.5 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ManageCreditCardsModal;
